import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../setup/redux/hooks";
import type { RootState } from "../../../../setup/redux/store";
import {
  StatusTabs,
  ProductStatus,
} from "../components/listing/shared/StatusTabs";
import {
  StoreFilter,
  StoreOption,
} from "../components/listing/shared/StoreFilter";
import {
  ProductTable,
  SortField,
  SortDirection,
} from "../components/listing/desktop/ProductTable";
import { ProductCard } from "../components/listing/mobile/ProductCard";
import type { ProductCardProps } from "../components/listing/mobile/ProductCard";
import {
  Product,
  ProductStatus as ProductStatusType,
  SyncStatus,
  ProductListingFilters,
} from "../types/ProductListing";
import {
  fetchProductListings,
  fetchAccessibleStores,
  setSearchTerm,
  setStatusFilter,
  setStoreFilter,
  setSortField,
  setPage,
  setPageSize,
  setFilters,
} from "../redux/productListingSlice";
import {
  copyMarketplaceStoreLink,
  openMarketplaceStore,
} from "../../../../util/Marketplace";
import { useTheme } from "src/context/ThemeContext";
import ContentHeader from "../../../layouts/content/ContentHeader";
import StockUpdateModal from "../components/modals/StockUpdateModal";
import type { StockUpdate } from "../components/modals/StockUpdateModal";
import PriceUpdateModal from "../components/modals/PriceUpdateModal";
import type { PriceUpdate } from "../types/ProductListing";
import ProductListingService from "../services/productListingService";
import { calculateProductTotalStock } from "../utils/stockCalculations";
import { ProductWizard } from "../components/wizard";
import {
  MerchantProductSelectionModal,
  MerchantProductBrowserModal,
  type MerchantProduct,
} from "src/shared/components";

function ProductListingPage() {
  const { theme } = useTheme();
  const dispatch = useAppDispatch();
  const {
    products,
    loading,
    error,
    filters,
    selectedProducts,
    stores,
    storesLoading,
    storesError,
    pagination,
  } = useSelector((state: RootState) => state.ProductListing);

  // Local state for UI components
  const [searchTerm, setSearchTermLocal] = useState<string>("");
  const [mobileItemsToShow, setMobileItemsToShow] = useState(10);
  const [hasInitialFetch, setHasInitialFetch] = useState(false);

  // Stock update modal state
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [selectedProductForStock, setSelectedProductForStock] =
    useState<Product | null>(null);

  // Price update modal state
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [selectedProductForPrice, setSelectedProductForPrice] =
    useState<Product | null>(null);

  // Product edit state
  const [selectedProductForEdit, setSelectedProductForEdit] =
    useState<Product | null>(null);

  // View state management
  const [currentView, setCurrentView] = useState<"list" | "create">("list");
  const [wizardMode, setWizardMode] = useState<"create" | "edit" | "view">(
    "create"
  );

  // Modal state for merchant product selection
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showBrowserModal, setShowBrowserModal] = useState(false);
  const [modalMode, setModalMode] = useState<"link" | "copy">("copy");

  // Initialize data on component mount
  useEffect(() => {
    // First, fetch accessible stores
    dispatch(fetchAccessibleStores() as any);
  }, [dispatch]);

  // Automatically fetch products when stores are auto-selected
  useEffect(() => {
    // Only fetch if we have stores selected, stores loading is complete, and we haven't done initial fetch
    if (
      filters.stores.length > 0 &&
      !storesLoading &&
      !loading &&
      !hasInitialFetch
    ) {
      setHasInitialFetch(true);
      dispatch(
        fetchProductListings({
          filters,
          page: pagination.page,
          pageSize: pagination.pageSize,
        }) as any
      );
    }
  }, [
    dispatch,
    filters.stores,
    storesLoading,
    loading,
    hasInitialFetch,
    pagination.page,
    pagination.pageSize,
  ]);

  // Sync local search term with Redux
  useEffect(() => {
    setSearchTermLocal(filters.search);
  }, [filters.search]);

  // Status mapping functions
  const apiToUiStatusMapping: Record<ProductStatusType, ProductStatus> = {
    all: "all",
    live: "active",
    draft: "draft",
    disabled: "disabled",
    banned: "banned",
    deleted: "deleted",
    sold_out: "soldOut",
    in_process: "inProcess",
    failed_publish: "failedPublish",
  };

  const uiToApiStatusMapping: Record<ProductStatus, ProductStatusType> = {
    all: "all",
    active: "live",
    draft: "draft",
    disabled: "disabled",
    banned: "banned",
    deleted: "deleted",
    draftMode: "draft",
    soldOut: "sold_out",
    inProcess: "in_process",
    failedPublish: "failed_publish",
  };

  // Convert stores to StoreOption format for the filter component
  const storeOptions: StoreOption[] = stores.map((store) => ({
    id: store.id,
    name: store.name,
    isActive: true, // All accessible stores are considered active
    count: 0, // We don't have count information from the API yet
    marketplace: store.marketplace, // Include marketplace info for icons
  }));

  // Get data from Redux store - use pagination.totalCount from API response
  const totalItems = pagination.totalCount || 0;

  // Local pagination and search handlers
  const handleSearchChange = (searchTerm: string) => {
    setSearchTermLocal(searchTerm);
    dispatch(setSearchTerm(searchTerm));
    dispatch(
      fetchProductListings({
        filters: { ...filters, search: searchTerm },
        page: 1,
        pageSize: pagination.pageSize,
      }) as any
    );
  };

  const handleSearch = (searchTerm: string) => {
    handleSearchChange(searchTerm);
  };

  const handleAddProduct = () => {
    setModalMode("copy");
    setShowSelectionModal(true);
  };

  const handleStockUpdate = (product: Product) => {
    setSelectedProductForStock(product);
    setStockModalOpen(true);
  };

  const handleStockModalClose = () => {
    setStockModalOpen(false);
    setSelectedProductForStock(null);
  };

  const handleStockSave = async (stockUpdates: StockUpdate[]) => {
    try {
      // Note: StockUpdateModal now calls the API directly with the new format
      // This callback is only used to refresh the product list after the modal's API call succeeds
      // If stockUpdates is empty, the modal already handled the API call

      // Refresh product listings after successful update
      dispatch(
        fetchProductListings({
          filters,
          page: pagination.page,
          pageSize: pagination.pageSize,
        }) as any
      );

      handleStockModalClose();
    } catch (error) {
      console.error("Failed to update stock:", error);
      throw error; // Re-throw to let modal handle error display
    }
  };

  const handlePriceUpdate = (product: Product) => {
    setSelectedProductForPrice(product);
    setPriceModalOpen(true);
  };

  const handlePriceModalClose = () => {
    setPriceModalOpen(false);
    setSelectedProductForPrice(null);
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProductForEdit(product);
    setWizardMode("edit");
    setCurrentView("create");
  };

  const handlePriceSave = async (priceUpdates: PriceUpdate[]) => {
    try {
      // Note: PriceUpdateModal already called the API with the correct endpoint
      // This callback is only used to refresh the product list after successful update

      // Refresh product listings to show updated prices from database
      dispatch(
        fetchProductListings({
          filters,
          page: pagination.page,
          pageSize: pagination.pageSize,
        }) as any
      );

      handlePriceModalClose();
    } catch (error) {
      console.error("Failed to refresh product list:", error);
      throw error;
    }
  };

  // Modal handlers for merchant product selection
  const handleCloseSelectionModal = () => {
    setShowSelectionModal(false);
  };

  const handleCloseBrowserModal = () => {
    setShowBrowserModal(false);
  };

  const handleCreateNewFromSelection = () => {
    setShowSelectionModal(false);
    setWizardMode("create");
    setCurrentView("create");
  };

  const handleOpenBrowserFromSelection = () => {
    setShowSelectionModal(false);
    setModalMode("copy");
    setShowBrowserModal(true);
  };

  const handleCopyMerchantProduct = (product: MerchantProduct) => {
    setShowBrowserModal(false);
    setWizardMode("create");
    setCurrentView("create");
    // TODO: Pass merchant product data to wizard for copying
    console.log("Copying merchant product:", product);
  };

  // Product wizard handlers
  const handleWizardClose = () => {
    setCurrentView("list");
    setSelectedProductForEdit(null);
  };

  const handleWizardSubmit = async (wizardData: any) => {
    try {
      console.log("Product wizard data:", wizardData);
      // TODO: Implement product creation/update API call
      // await ProductListingService.createProduct(wizardData);

      // Refresh the product list
      dispatch(
        fetchProductListings({
          filters,
          page: pagination.page,
          pageSize: pagination.pageSize,
        }) as any
      );

      handleWizardClose();
    } catch (error) {
      console.error("Failed to save product:", error);
      throw error; // Re-throw to let wizard handle error display
    }
  };

  const handleStatusChange = (status: ProductStatus) => {
    const apiStatus = uiToApiStatusMapping[status];
    dispatch(setStatusFilter(apiStatus));
    dispatch(
      fetchProductListings({
        filters: { ...filters, status: apiStatus },
        page: 1,
        pageSize: pagination.pageSize,
      }) as any
    );
  };

  const handleStoreSelectionChange = (storeIds: string[]) => {
    dispatch(setStoreFilter(storeIds));
    dispatch(
      fetchProductListings({
        filters: { ...filters, stores: storeIds },
        page: pagination.page,
        pageSize: pagination.pageSize,
      }) as any
    );
  };

  const handleSortChange = (field: SortField, direction: SortDirection) => {
    dispatch(setSortField({ field: field as any, direction }));
    dispatch(
      fetchProductListings({
        filters: { ...filters, sortBy: field as any, sortOrder: direction },
        page: pagination.page,
        pageSize: pagination.pageSize,
      }) as any
    );
  };

  const handleRowClick = (product: Product) => {
    console.log("Row clicked:", product.title);
  };

  const handlePageChange = (page: number) => {
    dispatch(setPage(page));
    dispatch(
      fetchProductListings({
        filters,
        page,
        pageSize: pagination.pageSize,
      }) as any
    );
  };

  const handlePageSizeChange = (newPageSize: number) => {
    dispatch(setPageSize(newPageSize));
    dispatch(
      fetchProductListings({
        filters,
        page: 1,
        pageSize: newPageSize,
      }) as any
    );
  };

  const handleLoadMore = () => {
    setMobileItemsToShow((prev) => Math.min(prev + 10, totalItems));
  };

  // Mobile action handlers
  const handleMobileAction = (action: string, productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    switch (action) {
      case "update-stock":
        handleStockUpdate(product);
        break;
      case "adjust-price":
        handlePriceUpdate(product);
        break;
      case "copy-link":
        // Use the same marketplace utility as desktop
        const store = stores.find((s) => s.id === product.accountId);
        copyMarketplaceStoreLink(product.marketplace, product, store);
        break;
      default:
        console.log(`Unknown mobile action: ${action} on product ${productId}`);
    }
  };

  // Use products from Redux store
  const paginatedProducts = products;

  // Calculate visible products for mobile
  const mobileVisibleProducts = products.slice(0, mobileItemsToShow);
  const hasMoreItems = mobileItemsToShow < totalItems;

  // Convert Product to ProductCardProps for mobile view
  const convertToCardProps = (product: Product): ProductCardProps => {
    // Calculate correct stock from inventory_allocation
    const calculatedStock = calculateProductTotalStock(product);

    // Create a placeholder store logo based on marketplace
    const getMarketplaceLogo = (marketplace: string) => {
      const logos = {
        shopee:
          "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=32&h=32&fit=crop",
        lazada:
          "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=32&h=32&fit=crop",
        tokopedia:
          "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=32&h=32&fit=crop",
        tiktok:
          "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=32&h=32&fit=crop",
      };
      return logos[marketplace as keyof typeof logos] || logos.shopee;
    };

    // Get store details same as desktop
    const getStoreDetails = (accountId: string, marketplace: string) => {
      const store = stores.find((s) => s.id === accountId);

      if (store) {
        return {
          name: store.name,
        };
      }

      // Fallback if store not found
      const marketplaceNames = {
        shopee: "Shopee",
        lazada: "Lazada",
        tokopedia: "Tokopedia",
        tiktok: "TikTok Shop",
      };
      return {
        name: `${
          marketplaceNames[marketplace as keyof typeof marketplaceNames] ||
          marketplace
        } Store`,
      };
    };

    return {
      id: product.id,
      name: product.title,
      sku: product.sku,
      image: product.mainImage,
      merchantProduct: product.merchantProduct,
      store: {
        name: getStoreDetails(product.accountId, product.marketplace).name,
        logo: getMarketplaceLogo(product.marketplace),
        marketplace: ["shopee", "lazada", "tokopedia"].includes(
          product.marketplace
        )
          ? (product.marketplace as "shopee" | "lazada" | "tokopedia")
          : "shopee", // Default fallback
      },
      price: {
        amount: product.price.current / 100, // Convert from cents to dollars
        currency: product.price.currency,
      },
      stock: {
        quantity: calculatedStock,
        status:
          calculatedStock === 0
            ? "out-of-stock"
            : calculatedStock < 50
            ? "low-stock"
            : "in-stock",
      },
      engagement: {
        likes: Math.floor(product.ratings.totalRatingCount * 0.3), // Estimate likes from reviews
        views: Math.floor(product.ratings.totalRatingCount * 2.5), // Estimate views from reviews
        rating: product.ratings.avgRating,
        reviewCount: product.ratings.totalRatingCount,
      },
      quality:
        product.sync.status === "success"
          ? "good"
          : product.sync.status === "pending"
          ? "incomplete"
          : "poor",
      syncStatus: product.sync.status,
      lastSyncTime: product.sync.lastSynced,
      onAction: handleMobileAction,
    };
  };

  return (
    <div data-testid="product-listing-page">
      {currentView === "list" && (
        <>
          {/* Page Header */}
          <ContentHeader
            title="Product Listing"
            searchValue={searchTerm}
            onSearchChange={handleSearchChange}
            onSearchSubmit={handleSearch}
            buttons={[
              {
                text: "Add Product Listing",
                onClick: handleAddProduct,
                variant: "primary",
                "data-testid": "add-product-button",
                className:
                  "tw-min-w-[84px] tw-max-w-[125px] tw-overflow-hidden tw-leading-normal",
              },
            ]}
          >
            <div className="tw-flex tw-flex-col tw-gap-4 md:tw-hidden">
              {/* Mobile Store Filter */}
              <div className="tw-flex tw-gap-3 tw-overflow-x-auto tw-scrollbar-hide tw-px-4">
                <StoreFilter
                  stores={storeOptions}
                  selectedStores={filters.stores}
                  onSelectionChange={handleStoreSelectionChange}
                  placeholder={
                    storesLoading ? "Loading stores..." : "All Stores"
                  }
                />
                {storesError && (
                  <div className="tw-text-sm tw-text-red-600">
                    Error loading stores: {storesError}
                  </div>
                )}
              </div>

              {/* Mobile Status Tabs */}
              <div className="tw-overflow-x-auto tw-scrollbar-hide tw-px-4">
                <div className="tw-flex tw-border-b tw-border-slate-200 dark:tw-border-slate-700">
                  <StatusTabs
                    activeStatus={apiToUiStatusMapping[filters.status] || "all"}
                    onStatusChange={handleStatusChange}
                    tabs={[
                      { key: "all", label: "All", count: 856 },
                      { key: "active", label: "Live", count: 234 },
                      { key: "inProcess", label: "In Process", count: 45 },
                      {
                        key: "failedPublish",
                        label: "Failed Publish",
                        count: 67,
                      },
                      { key: "disabled", label: "Disabled", count: 144 },
                      { key: "soldOut", label: "Sold Out", count: 89 },
                      { key: "banned", label: "Banned", count: 12 },
                      { key: "deleted", label: "Deleted", count: 23 },
                      { key: "draftMode", label: "Draft", count: 34 },
                    ]}
                  />
                </div>
              </div>
            </div>
          </ContentHeader>

          {/* Main content area */}
          <div
            className={`tw-flex-1 tw-overflow-y-auto tw-p-4 tw-pb-20 md:tw-p-6 ${
              theme === "light" ? "tw-bg-gray-50" : "tw-bg-slate-900"
            }`}
          >
            <div className="tw-flex tw-flex-col tw-gap-4">
              {/* Desktop Store Filter & Status Tabs - Only visible on desktop */}
              <div className="tw-hidden md:tw-block tw-space-y-4">
                {/* Desktop Store Filter */}
                <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap tw-relative">
                  <StoreFilter
                    stores={storeOptions}
                    selectedStores={filters.stores}
                    onSelectionChange={handleStoreSelectionChange}
                    placeholder={
                      storesLoading ? "Loading stores..." : "All Stores"
                    }
                  />
                  {storesError && (
                    <div className="tw-text-sm tw-text-red-600">
                      Error loading stores: {storesError}
                    </div>
                  )}
                </div>

                {/* Desktop Status Tabs */}
                <div className="tw-overflow-x-auto tw-scrollbar-hide">
                  <div className="tw-flex tw-border-b tw-border-slate-200 dark:tw-border-slate-700">
                    <StatusTabs
                      activeStatus={
                        apiToUiStatusMapping[filters.status] || "all"
                      }
                      onStatusChange={handleStatusChange}
                      tabs={[
                        { key: "all", label: "All", count: 856 },
                        { key: "active", label: "Live", count: 234 },
                        { key: "inProcess", label: "In Process", count: 45 },
                        {
                          key: "failedPublish",
                          label: "Failed Publish",
                          count: 67,
                        },
                        { key: "disabled", label: "Disabled", count: 144 },
                        { key: "soldOut", label: "Sold Out", count: 89 },
                        { key: "banned", label: "Banned", count: 12 },
                        { key: "deleted", label: "Deleted", count: 23 },
                        { key: "draftMode", label: "Draft", count: 34 },
                      ]}
                    />
                  </div>
                </div>
              </div>

              {/* Product Table Section - Desktop */}
              <div className="tw-hidden md:tw-block tw-overflow-x-auto">
                <ProductTable
                  products={paginatedProducts}
                  stores={stores}
                  loading={loading}
                  onSortChange={handleSortChange}
                  onRowClick={handleRowClick}
                  onStockUpdate={handleStockUpdate}
                  onPriceUpdate={handlePriceUpdate}
                  onEditProduct={handleEditProduct}
                  sortField={filters.sortBy as SortField}
                  sortDirection={filters.sortOrder as SortDirection}
                  showVariants={true}
                  pagination={{
                    currentPage: pagination.page,
                    totalItems: totalItems,
                    pageSize: pagination.pageSize,
                    onPageChange: handlePageChange,
                    onPageSizeChange: handlePageSizeChange,
                    itemName: "product listing",
                  }}
                />
              </div>
            </div>

            {/* Mobile Product Cards with Load More - Hidden on Desktop */}
            <div
              className="tw-block md:tw-hidden tw-flex-1 tw-overflow-y-auto"
              data-testid="mobile-products-container"
            >
              {loading ? (
                /* Mobile Loading State */
                <div className="tw-flex tw-flex-col tw-gap-4">
                  {Array.from({ length: 3 }, (_, index) => (
                    <div
                      key={index}
                      className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-animate-pulse"
                    >
                      <div className="tw-flex tw-gap-3">
                        <div className="tw-h-16 tw-w-16 tw-bg-gray-200 tw-rounded tw-flex-shrink-0"></div>
                        <div className="tw-flex-1 tw-space-y-2">
                          <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-3/4"></div>
                          <div className="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-1/2"></div>
                          <div className="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-1/4"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : products.length === 0 ? (
                /* Mobile Empty State */
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-py-16 tw-text-center tw-min-h-[50vh]">
                  <div className="tw-mb-6 tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-50">
                    <span className="material-symbols-outlined tw-text-5xl tw-text-gray-400">
                      search_off
                    </span>
                  </div>
                  <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-2">
                    No Products Found
                  </h3>
                  <p className="tw-text-sm tw-text-gray-500 tw-mb-6 tw-max-w-sm">
                    Your search criteria didn't match any products. Try clearing
                    filters or adding a new product.
                  </p>
                  <button
                    onClick={handleAddProduct}
                    className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary tw-px-6 tw-py-3 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm hover:tw-bg-primary/90 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-ring-offset-2"
                  >
                    <span className="material-symbols-outlined tw-mr-2 tw-text-xl">
                      add_circle
                    </span>
                    Add Product
                  </button>
                </div>
              ) : (
                <div
                  className="tw-flex tw-flex-col tw-gap-4"
                  data-testid="product-cards-container"
                >
                  {mobileVisibleProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      {...convertToCardProps(product)}
                    />
                  ))}

                  {/* Load More Button */}
                  {hasMoreItems && (
                    <div className="tw-flex tw-justify-center tw-pt-4">
                      <button
                        onClick={handleLoadMore}
                        className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-3 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-shadow-sm"
                      >
                        <span className="material-symbols-outlined tw-text-base">
                          expand_more
                        </span>
                        Load More ({totalItems - mobileItemsToShow} remaining)
                      </button>
                    </div>
                  )}

                  {/* Mobile Items Count */}
                  <div className="tw-text-center tw-text-sm tw-text-gray-500 tw-pt-2">
                    Showing {mobileVisibleProducts.length} of {totalItems}{" "}
                    products
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Product Wizard View - Full Page Overlay */}
      {currentView === "create" && (
        <ProductWizard
          mode={wizardMode}
          productId={selectedProductForEdit?.id || null}
          onClose={handleWizardClose}
          onSubmit={handleWizardSubmit}
        />
      )}

      {/* Stock Update Modal */}
      <StockUpdateModal
        isOpen={stockModalOpen}
        onClose={handleStockModalClose}
        product={selectedProductForStock}
        onSave={handleStockSave}
        stores={stores}
      />

      {/* Price Update Modal */}
      <PriceUpdateModal
        isOpen={priceModalOpen}
        onClose={handlePriceModalClose}
        product={selectedProductForPrice}
        onSave={handlePriceSave}
        stores={stores}
      />

      {/* Merchant Product Selection Modal */}
      <MerchantProductSelectionModal
        isOpen={showSelectionModal}
        onClose={handleCloseSelectionModal}
        onCreateNew={handleCreateNewFromSelection}
        onOpenBrowser={handleOpenBrowserFromSelection}
        mode={modalMode}
      />

      {/* Merchant Product Browser Modal */}
      <MerchantProductBrowserModal
        isOpen={showBrowserModal}
        onClose={handleCloseBrowserModal}
        onSelectProduct={() => {}} // Not used in copy mode
        onCopyProduct={handleCopyMerchantProduct}
        mode={modalMode}
        title="Copy from Merchant Product"
        showBackButton={true}
        onBack={() => {
          setShowBrowserModal(false);
          setShowSelectionModal(true);
        }}
      />
    </div>
  );
}

export default ProductListingPage;
